/* eslint-disable @next/next/no-img-element */
import { ElementProps } from '@/components/sections/builder/types';
import {
  ImageBannerElementDataType,
  ImageBannerSubtitleElementDataType,
} from '@/components/sections/builder/elements/body/image-banner/types';
import { WrapperChild } from '@/components/sections/builder/wrapper';
import clsx from 'clsx';
import { buildBackgroundWithOpacity } from '@/components/sections/builder/settings/background-setting';
import { useEffect, useState } from 'react';
import useMediaQuery from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

export default function ElementContent({ element, preview }: ElementProps) {
  const [zoom, setZoom] = useState(100);
  const childs = element.childs;
  const data = element.data as ImageBannerElementDataType;
  const subtitleChild = childs?.find((e) => e?.type === 'image_banner_subtitle')
    ?.data as ImageBannerSubtitleElementDataType;

  const backgroundAnimationClass = `background_animation-${data.backgroundAnimation || 'none'}`;

  const isMobile = useMediaQuery(data.mobileMinWidth);
  const firstImage = isMobile && data.firstImageMobile ? data.firstImageMobile : data.firstImage;
  const secondImage =
    isMobile && data.secondImageMobile ? data.secondImageMobile : data.secondImage;

  const handleScroll = () => {
    const bannerElement = document.getElementById(`image_banner-${element.id}`);
    const offsetTop =
      (bannerElement?.getBoundingClientRect()?.top || 0) - (window.innerHeight - 120);
    const height = bannerElement?.getBoundingClientRect()?.height || 0;

    const scrollPercentage = offsetTop < 0 ? ((offsetTop * -1) / height) * 100 : 0;
    const zoomFactor = 100 + scrollPercentage;

    setZoom(zoomFactor);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const images = [firstImage, secondImage].filter((v) => !!v);

  return (
    <>
      <section className='container-padding relative mx-auto bg-white'>
        <div
          id={`image_banner-${element.id}`}
          className={clsx(
            'h-full',
            data.height === 'adapt' && isMobile && 'relative w-full',
            data.height === 'small' &&
              'flex min-h-[280px] w-full flex-row items-center md:min-h-[420px]',
            data.height === 'medium' &&
              'flex min-h-[340px] w-full flex-row items-center md:min-h-[560px]',
            data.height === 'large' &&
              'flex min-h-[390px] w-full flex-row items-center md:min-h-[720px]'
          )}
        >
          {data.height === 'adapt' && (
            <img
              src={images[0]}
              alt='image-background'
              className={clsx(
                'h-full opacity-0 transition-all',
                subtitleChild?.label.length >= 400 && 'absolute',
                images[1] && data.stackImageMobile && 'w-full md:w-1/2',
                images[1] && !data.stackImageMobile && 'w-1/2',
                !images[1] && 'w-full'
              )}
            />
          )}
          <div className='absolute inset-0 flex gap-0 transition-all duration-75'>
            {images[0] && (
              <div className='m-0 h-full w-full overflow-hidden p-0'>
                <div
                  className={clsx(
                    'background-first-image h-full w-full',
                    backgroundAnimationClass,
                    data.backgroundAnimation === 'ambient_movement' && 'animate-zoomSlideDown'
                  )}
                />
              </div>
            )}
            {images[1] && (
              <div
                className={clsx(
                  'm-0 h-full w-full overflow-hidden p-0',
                  data.stackImageMobile && 'hidden md:block'
                )}
              >
                <div
                  className={clsx(
                    'background-second-image h-full w-full',
                    backgroundAnimationClass,
                    data.backgroundAnimation === 'ambient_movement' && 'animate-zoomSlideDown'
                  )}
                />
              </div>
            )}
          </div>
          {data.overlay && <div className={clsx('overlay absolute inset-0')} />}
          <div
            className={cn(
              'inset-0 w-full',
              subtitleChild?.label.length >= 400 ||
                !['medium', 'large', 'adapt'].includes(data.height as string)
                ? 'relative'
                : 'absolute'
            )}
          >
            <div
              className={clsx(
                'theme_layout_page_width relative mx-auto flex h-full w-full duration-100 @xl:gap-0 md:px-4 md:py-8 lg:gap-8 lg:px-8 ',
                data.contentPosition === 'top-left' && 'items-start justify-start',
                data.contentPosition === 'top-center' && 'items-start justify-center',
                data.contentPosition === 'top-right' && 'items-start justify-end',
                data.contentPosition === 'middle-left' && 'items-center justify-start',
                data.contentPosition === 'middle-center' && 'items-center justify-center',
                data.contentPosition === 'middle-right' && 'items-center justify-end',
                data.contentPosition === 'bottom-left' && 'items-end justify-start',
                data.contentPosition === 'bottom-center' && 'items-end justify-center',
                data.contentPosition === 'bottom-right' && 'items-end justify-end',
                data.contentAlign === 'left' && 'md:text-left',
                data.contentAlign === 'center' && 'md:text-center',
                data.contentAlign === 'right' && 'md:text-right',
                data.contentAlignMobile === 'left' && 'text-left',
                data.contentAlignMobile === 'center' && 'text-center',
                data.contentAlignMobile === 'right' && 'text-right'
              )}
            >
              <div className={clsx('bg_container flex w-full flex-col p-3 md:w-fit')}>
                {(childs || []).map((child) => (
                  <WrapperChild
                    child={{
                      ...child,
                      parentData: data,
                    }}
                    parentType={element.type}
                    key={child.id}
                    preview={preview}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        {data.stackImageMobile && images[1] && (
          <div className='relative block md:hidden'>
            {data.height === 'adapt' && (
              <img
                src={images[0]}
                alt='image-background'
                className={clsx('h-full opacity-0', images[1] ? 'w-full md:w-1/2' : 'w-full')}
              />
            )}
            <div className='absolute inset-0 flex gap-0'>
              <div className='m-0 h-full w-full overflow-hidden p-0'>
                <div
                  className={clsx(
                    'background-second-image h-full w-full',
                    backgroundAnimationClass,
                    data.backgroundAnimation === 'ambient_movement' && 'animate-zoomSlideDown'
                  )}
                />
              </div>
            </div>
          </div>
        )}
      </section>

      <style jsx>
        {`
          .container-padding {
            padding-top: ${data.padding?.top || 0}px;
            padding-bottom: ${data.padding?.bottom || 0}px;
            margin-left: ${data.padding?.left || 0}px;
            margin-right: ${data.padding?.right || 0}px;
          }

          @media (min-width: 768px) {
            .bg_container {
              background-color: ${data.showContainerDesktop
                ? data.containerColorScheme?.background || 'white'
                : 'transparent'};
            }
          }

          .bg_container {
            background-color: ${data.showContainerMobile
              ? data.containerColorScheme?.background || 'white'
              : 'transparent'};
          }

          .overlay {
            background-color: ${buildBackgroundWithOpacity(
              data.overlay as string,
              data.overlayOpacity
            )};
          }

          .background_animation-zoom_on_scroll {
            background-size: cover;
            transform: scale(${zoom / 100});
          }

          .background_animation-fixed {
            background-size: cover;
            background-attachment: fixed;
          }

          .background_animation-ambient_movement {
            background-size: cover;
          }

          .background_animation-none {
            background-size: cover;
          }

          .background-first-image {
            background-image: url(${images[0]});
            background-repeat: no-repeat;
            background-position: center;
          }

          .background-second-image {
            background-image: url(${images[1]});
            background-repeat: no-repeat;
            background-position: center;
          }
        `}
      </style>
    </>
  );
}
